// src/pages/automations/automationApi.ts
import { BASE_URL } from "../../utils/constants";

interface AutomationTrigger {
    type: string;
    conditions?: {
        op: 'AND' | 'OR';
        conditions: Array<{
            entity: string;
            field: string;
            operator: string;
            value?: string | null;
        }>;
    };
}

interface AutomationGraphNode {
    id: string;
    type: string;
    cfg: Record<string, any>;
    edges: {
        next?: string;
        true?: string;
        false?: string;
    };
}

interface AutomationGraph {
    nodes: Record<string, AutomationGraphNode>;
}

interface AutomationEntryPolicy {
    reentry: 'MULTIPLE' | 'ONCE' | 'WINDOW';
    windowDays?: number | null;
}

interface CreateAutomationRequest {
    name: string;
    description?: string;
    trigger: AutomationTrigger;
    graph: AutomationGraph;
    entryPolicy: AutomationEntryPolicy;
}

interface CreateAutomationResponse {
    id: string;
    name: string;
    status: string;
    [key: string]: any;
}

// interface Bucket {
//     name?: string;
//     weight: number;
//     to?: string;
// }

// Generate a unique Idempotency-Key
const generateIdempotencyKey = (): string => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 10);
    return `${timestamp}-${random}`;
};

// Helper function to build conditions from selected conditions (NO hardcoded fake values)
const buildConditions = (selectedConditions: any[]): { op: 'AND' | 'OR'; conditions: Array<{ entity: string; field: string; operator: string; value?: string | null; }> } | undefined => {
    if (!selectedConditions || selectedConditions.length === 0) {
        return undefined;
    }

    const conditions = selectedConditions.map(cond => {
        let entity = '';
        let field = '';

        if (['email_subscription', 'email_engagement', 'email_status', 'email_interaction'].includes(cond.category)) {
            entity = 'Email';
            field = cond.category;
        } else if (['first_name', 'last_name', 'email'].includes(cond.category)) {
            entity = 'Contact';
            field = cond.category;
        } else {
            entity = cond.entity || 'Contact';
            field = cond.category || cond.field || '';
        }

        return {
            entity: entity,
            field: field,
            operator: cond.operator || 'EXISTS',
            value: cond.value !== undefined ? cond.value : null
        };
    });

    return {
        op: 'AND',
        conditions: conditions
    };
};

// Map internal node types to API node types
const getNodeType = (internalType: string): string => {
    switch (internalType) {
        case 'tags':
            return 'TAG_ADD';
        case 'send':
            return 'SEND_EMAIL';
        case 'time':
            return 'WAIT_UNTIL';
        case 'wait':
            return 'WAIT_UNTIL';
        case 'branch':
            return 'BRANCH_TRUE_FALSE';
        case 'multi':
            return 'MULTI_SPLIT';
        case 'percent':
            return 'PERCENTAGE_SPLIT';
        case 'exit':
            return 'EXIT';
        default:
            return 'EXIT';
    }
};

// Ensure the graph has at least one EXIT node
const ensureExitNode = (nodes: any[], edges: any[]): { nodes: any[], edges: any[] } => {
    const hasExitNode = nodes.some((node) => node.data.type === 'exit');

    if (!hasExitNode) {
        const lastNode = nodes[nodes.length - 1];

        const exitNode = {
            id: 'nEXIT',
            type: 'custom',
            data: {
                label: 'Exit',
                type: 'exit',
                id: 'nEXIT',
            },
            position: { x: 0, y: 0 },
        };

        if (lastNode && lastNode.id !== 'home') {
            edges.push({
                id: `e${lastNode.id}-nEXIT`,
                source: lastNode.id,
                target: 'nEXIT',
            });
        }

        nodes.push(exitNode);
    }

    return { nodes, edges };
};

// Convert your node structure to the API format
export const convertNodesToApiFormat = (nodes: any[], edges: any[]): AutomationGraph => {
    const graphNodes: Record<string, AutomationGraphNode> = {};

    let tagCounter = 0;
    let sendCounter = 0;
    let waitCounter = 0;
    let exitCounter = 0;
    let branchCounter = 0;
    let splitCounter = 0;
    let percentageCounter = 0;

    // Map internal node IDs to simpler API-friendly IDs
    const idMap: Record<string, string> = {};

    // First pass: assign simple IDs
    nodes.forEach((node) => {
        const isHome = node.id === 'home';
        if (isHome) {
            idMap[node.id] = 'nTRIGGER';
            return;
        }

        let simpleId = '';
        switch (node.data.type) {
            case 'tags':
                tagCounter++;
                simpleId = `nTAG${tagCounter}`;
                break;
            case 'send':
                sendCounter++;
                simpleId = `nSEND${sendCounter}`;
                break;
            case 'wait':
            case 'time':
                waitCounter++;
                simpleId = `nWAIT${waitCounter}`;
                break;
            case 'exit':
                exitCounter++;
                simpleId = `nEXIT${exitCounter}`;
                break;
            case 'branch':
                branchCounter++;
                simpleId = `nBRANCH${branchCounter}`;
                break;
            case 'multi':
                splitCounter++;
                simpleId = `nSPLIT${splitCounter}`;
                break;
            case 'percent':
                percentageCounter++;
                simpleId = `nPERCENT${percentageCounter}`;
                break;
            default:
                simpleId = `nNODE_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
        }
        idMap[node.id] = simpleId;
    });

    // Second pass: build graph nodes with mapped IDs
    nodes.forEach((node) => {
        const isHome = node.id === 'home';
        const nodeType = isHome ? 'TRIGGER' : getNodeType(node.data.type);
        const mappedId = idMap[node.id];

        if (isHome) {
            graphNodes[mappedId] = {
                id: mappedId,
                type: 'TRIGGER',
                cfg: {},
                edges: {}
            };
            return;
        }

        let cfg: Record<string, any> = {};

        if (node.data.type === 'tags' && node.data.tag) {
            const tags = Array.isArray(node.data.selectedTags) && node.data.selectedTags.length > 0
                ? node.data.selectedTags
                : (typeof node.data.tag === 'string' && node.data.tag.includes(','))
                    ? node.data.tag.split(',').map((t: string) => t.trim()).filter(Boolean)
                    : [node.data.tag];
            cfg = { tags: tags };
        } else if (node.data.type === 'send') {
            const campaignId = node.data.template || node.data.variantId || '';

            cfg = {
                campaignId: campaignId,
                variantId: campaignId,
                guards: {
                    requireSubscribed: true
                }
            };
        } else if (node.data.type === 'wait' || node.data.type === 'time') {
            const amount = node.data.maxWaitAmount || node.data.amount || 1;
            const unit = node.data.maxWaitUnit || node.data.unit || 'days';

            let days = 0;
            let hours = 0;
            let minutes = 0;

            switch (unit) {
                case 'days':
                    days = amount;
                    break;
                case 'weeks':
                    days = amount * 7;
                    break;
                case 'months':
                    days = amount * 30;
                    break;
                case 'hours':
                    hours = amount;
                    break;
                case 'minutes':
                    minutes = amount;
                    break;
                default:
                    days = amount;
            }

            cfg = {
                delay: {
                    days: days,
                    hours: hours,
                    minutes: minutes
                }
            };
        } else if (node.data.type === 'branch') {
            // Use branchConditions from node.data
            const branchConditions = node.data.branchConditions || [];
            const conditionLogic = node.data.conditionLogic || 'AND';

            // Build the condition object
            const conditions = branchConditions.map((cond: any) => {
                let entity = cond.entity || 'Contact';
                let field = cond.field || '';

                // Map field names to proper entity
                if (['email_subscription', 'email_engagement', 'email_status', 'email_interaction'].includes(field)) {
                    entity = 'Email';
                } else if (['first_name', 'last_name', 'email'].includes(field)) {
                    entity = 'Contact';
                }

                return {
                    entity: entity,
                    field: field,
                    operator: cond.operator || 'EXISTS',
                    value: cond.value !== undefined ? cond.value : null
                };
            });

            cfg = {
                condition: {
                    op: conditionLogic,
                    conditions: conditions.length > 0 ? conditions : []
                }
            };
        } else if (node.data.type === 'multi') {
            const rules = node.data.multiSplitRules || [];

            // 1. Identify Default rule (Index 0 / label 'Default')
            const defaultRule = rules.find((r: any, idx: number) => idx === 0 || r.label === 'Default' || r.id === 'rule-default') || rules[0];

            let defaultTargetId = '';
            if (defaultRule) {
                const defaultEdge = edges.find((e: any) =>
                    e.source === node.id && e.sourceHandle === `rule-${defaultRule.id}`
                );
                defaultTargetId = defaultEdge ? (idMap[defaultEdge.target] || defaultEdge.target) : (idMap[defaultRule.to] || defaultRule.to || idMap[node.data.defaultTo] || node.data.defaultTo || '');
            }

            // 2. Condition rules (ONLY actual Condition 1, Condition 2, etc. — strictly excluding Default)
            const conditionRules = rules
                .filter((r: any, idx: number) => idx !== 0 && r !== defaultRule && r.label !== 'Default' && r.id !== 'rule-default')
                .map((rule: any) => {
                    const edge = edges.find((e: any) =>
                        e.source === node.id && e.sourceHandle === `rule-${rule.id}`
                    );
                    const targetId = edge ? (idMap[edge.target] || edge.target) : (idMap[rule.to] || rule.to || '');

                    let condition: any = null;
                    if (typeof rule.condition === 'object' && rule.condition !== null) {
                        condition = {
                            entity: rule.condition.entity || 'Contact',
                            field: rule.condition.field || '',
                            operator: rule.condition.operator || 'EXISTS',
                            value: rule.condition.value !== undefined ? rule.condition.value : null
                        };
                    } else if (typeof rule.condition === 'string' && rule.condition.trim() !== '') {
                        condition = {
                            entity: ['email_subscription', 'email_engagement', 'email_status', 'email_interaction'].includes(rule.condition) ? 'Email' : 'Contact',
                            field: rule.condition,
                            operator: 'EXISTS',
                            value: null
                        };
                    }

                    return {
                        condition: condition,
                        to: targetId
                    };
                })
                .filter((r: any) => r.condition !== null);

            cfg = {
                rules: conditionRules,
                defaultTo: defaultTargetId || null
            };
        } else if (node.data.type === 'percent') {
            const outgoingEdges = edges.filter((edge: any) => edge.source === node.id);
            const buckets = node.data.buckets || [
                { name: 'A', weight: 50 },
                { name: 'B', weight: 50 }
            ];

            const bucketsWithTargets = buckets.map((bucket: any, index: number) => {
                let edge = outgoingEdges.find((e: any) => {
                    if (e.sourceHandle) {
                        return e.sourceHandle === `percent-${index}`;
                    }
                    return false;
                });

                if (!edge && bucket.to) {
                    edge = outgoingEdges.find((e: any) => e.target === bucket.to);
                }

                if (!edge && outgoingEdges.length > 0) {
                    if (index < outgoingEdges.length) {
                        edge = outgoingEdges[index];
                    } else {
                        edge = outgoingEdges[outgoingEdges.length - 1];
                    }
                }

                let targetId = '';
                if (edge) {
                    targetId = idMap[edge.target] || '';
                } else if (bucket.to) {
                    targetId = idMap[bucket.to] || bucket.to;
                }

                if (!targetId) {
                    const exitNode = nodes.find((n: any) => n.data.type === 'exit');
                    if (exitNode) {
                        targetId = idMap[exitNode.id] || '';
                    }
                }

                return {
                    weight: Number(bucket.weight) || 0,
                    to: targetId
                };
            });

            cfg = {
                buckets: bucketsWithTargets
            };
        } else if (node.data.type === 'exit') {
            cfg = {};
        }

        const outgoingEdges = edges.filter((edge: any) => edge.source === node.id);
        const edgesConfig: { next?: string; true?: string; false?: string } = {};

        if (node.data.type === 'branch') {
            outgoingEdges.forEach((edge: any) => {
                const targetId = idMap[edge.target];
                if (edge.sourceHandle === 'true') {
                    edgesConfig.true = targetId;
                } else if (edge.sourceHandle === 'false') {
                    edgesConfig.false = targetId;
                }
            });
        } else if (node.data.type === 'multi') {
            // Handled in cfg.rules & cfg.defaultTo
        } else if (node.data.type === 'percent') {
            // Handled in cfg.buckets
        } else if (node.data.type === 'exit') {
            // EXIT has no outgoing edges
        } else if (outgoingEdges.length > 0) {
            const targetId = idMap[outgoingEdges[0].target];
            if (targetId) {
                edgesConfig.next = targetId;
            }
        }

        graphNodes[mappedId] = {
            id: mappedId,
            type: nodeType,
            cfg: cfg,
            edges: edgesConfig
        };
    });

    const triggerNode = nodes.find(n => n.id === 'home');
    if (triggerNode) {
        const triggerId = idMap[triggerNode.id] || 'nTRIGGER';
        const outgoingEdges = edges.filter((edge: any) => edge.source === triggerNode.id);

        if (outgoingEdges.length > 0 && graphNodes[triggerId]) {
            const targetId = idMap[outgoingEdges[0].target];
            if (targetId) {
                graphNodes[triggerId].edges = { next: targetId };
            }
        }
    }

    return { nodes: graphNodes };
};

// Get the trigger type from your internal trigger value
export const getTriggerType = (trigger: string): string => {
    switch (trigger) {
        case 'form_submission':
            return 'FormSubmission';
        case 'new_subscriber':
            return 'NewSubscriber';
        case 'email_opened':
            return 'EmailOpened';
        case 'link_clicked':
            return 'LinkClicked';
        case 'scheduled_date_time':
            return 'ScheduledDateTime';
        case 'EmailSubscription':
            return 'EmailSubscription';
        case 'EmailEngagement':
            return 'EmailEngagement';
        case 'EmailStatus':
            return 'EmailStatus';
        case 'AudienceChange':
            return 'AudienceChange';
        default:
            return 'FormSubmission';
    }
};

// ============================================================
// CREATE AUTOMATION DRAFT (POST /api/v1/automations/draft)
// ============================================================
export const createAutomationDraft = async (
    name: string,
    trigger: string,
    nodes: any[],
    edges: any[],
    token: string,
    tenantId: string,
    userId: string,
    baseUrl: string = BASE_URL,
    selectedConditions: any[] = []
): Promise<CreateAutomationResponse> => {

    if (!name || !name.trim()) {
        throw new Error('Automation name is required');
    }

    if (!trigger) {
        throw new Error('Trigger is required');
    }

    if (!nodes || nodes.length === 0) {
        throw new Error('Graph nodes are required');
    }

    const { nodes: updatedNodes, edges: updatedEdges } = ensureExitNode(
        JSON.parse(JSON.stringify(nodes)),
        JSON.parse(JSON.stringify(edges))
    );

    const convertedGraph = convertNodesToApiFormat(updatedNodes, updatedEdges);

    const requestBody: CreateAutomationRequest = {
        name: name.trim(),
        description: '',
        trigger: {
            type: getTriggerType(trigger),
            ...(buildConditions(selectedConditions) ? { conditions: buildConditions(selectedConditions) } : {})
        },
        graph: convertedGraph,
        entryPolicy: {
            reentry: 'MULTIPLE',
            windowDays: null
        }
    };


    const idempotencyKey = generateIdempotencyKey();
    const url = `${baseUrl}/api/v1/automations/draft`;

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Idempotency-Key': idempotencyKey,
                'X-Tenant-Id': tenantId,
                'X-User-Id': userId || '',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            let errorMessage = `Failed to create automation draft (${response.status})`;
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } catch {
                errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();
        return data;

    } catch (error) {
        // console.error('❌ Error creating automation draft:', error);
        throw error;
    }
};

// ============================================================
// UPDATE AUTOMATION DRAFT (PUT /api/v1/automations/{id}/draft)
// ============================================================
export const updateAutomationDraft = async (
    automationId: string,
    name: string,
    trigger: string,
    nodes: any[],
    edges: any[],
    token: string,
    tenantId: string,
    userId: string,
    baseUrl: string = BASE_URL,
    selectedConditions: any[] = []
): Promise<CreateAutomationResponse> => {

    if (!automationId) {
        throw new Error('Automation ID is required');
    }

    if (!name || !name.trim()) {
        throw new Error('Automation name is required');
    }

    if (!trigger) {
        throw new Error('Trigger is required');
    }

    if (!nodes || nodes.length === 0) {
        throw new Error('Graph nodes are required');
    }

    const { nodes: updatedNodes, edges: updatedEdges } = ensureExitNode(
        JSON.parse(JSON.stringify(nodes)),
        JSON.parse(JSON.stringify(edges))
    );

    const requestBody: CreateAutomationRequest = {
        name: name.trim(),
        description: '',
        trigger: {
            type: getTriggerType(trigger),
            ...(buildConditions(selectedConditions) ? { conditions: buildConditions(selectedConditions) } : {})
        },
        graph: convertNodesToApiFormat(updatedNodes, updatedEdges),
        entryPolicy: {
            reentry: 'MULTIPLE',
            windowDays: null
        }
    };

    const idempotencyKey = generateIdempotencyKey();
    const url = `${baseUrl}/api/v1/automations/${automationId}/draft`;

    try {
        const response = await fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Idempotency-Key': idempotencyKey,
                'X-Tenant-Id': tenantId,
                'X-User-Id': userId || '',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            let errorMessage = `Failed to update automation draft (${response.status})`;
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } catch {
                errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();
        return data;

    } catch (error) {
        // console.error('❌ Error updating automation draft:', error);
        throw error;
    }
};

// ============================================================
// PUBLISH AUTOMATION (POST /api/v1/automations/{id}/publish)
// ============================================================
export const publishAutomation = async (
    automationId: string,
    token: string,
    tenantId: string,
    userId: string,
    baseUrl: string = BASE_URL
): Promise<CreateAutomationResponse> => {

    if (!automationId) {
        throw new Error('Automation ID is required');
    }

    const idempotencyKey = generateIdempotencyKey();
    const url = `${baseUrl}/api/v1/automations/${automationId}/publish`;

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Idempotency-Key': idempotencyKey,
                'X-Tenant-Id': tenantId,
                'X-User-Id': userId || '',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            let errorMessage = `Failed to publish automation (${response.status})`;
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } catch {
                errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();
        return data;

    } catch (error) {
        // console.error('❌ Error publishing automation:', error);
        throw error;
    }
};

// ============================================================
// GET AUTOMATION BY ID (GET /api/v1/automations/{id})
// ============================================================
export const getAutomationById = async (
    automationId: string,
    token: string,
    tenantId: string,
    userId: string,
    baseUrl: string = BASE_URL
): Promise<any> => {

    if (!automationId) {
        throw new Error('Automation ID is required');
    }

    const url = `${baseUrl}/api/v1/automations/${automationId}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Tenant-Id': tenantId,
                'X-User-Id': userId || '',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            let errorMessage = `Failed to get automation (${response.status})`;
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } catch {
                errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();
        return data;

    } catch (error) {
        // console.error('❌ Error getting automation:', error);
        throw error;
    }
};

// ============================================================
// DELETE AUTOMATION (DELETE /api/v1/automations/{id})
// ============================================================
export const deleteAutomation = async (
    automationId: string,
    token: string,
    tenantId: string,
    userId: string,
    baseUrl: string = BASE_URL
): Promise<void> => {

    if (!automationId) {
        throw new Error('Automation ID is required');
    }

    const url = `${baseUrl}/api/v1/automations/${automationId}`;

    try {
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'X-Tenant-Id': tenantId,
                'X-User-Id': userId || '',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            let errorMessage = `Failed to delete automation (${response.status})`;
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } catch {
                errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
        }

    } catch (error) {
        // console.error('❌ Error deleting automation:', error);
        throw error;
    }
};